import { useEffect, useState } from 'react';
import { Checkbox } from '../../../components/Checkbox';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { TextInput } from '../../../components/TextInput';
import type { BillingInvoice, ReverseInvoicePaymentRequest } from '../../../types/adminBilling';
import { centsToDollarsInput, dollarsToCents, formatMoney } from './billingFormat';

interface ReverseInvoicePaymentModalProps {
  invoice: BillingInvoice | null;
  saving: boolean;
  error: string | null;
  onConfirm: (request: ReverseInvoicePaymentRequest) => void;
  onClose: () => void;
}

/**
 * Take back money recorded against an invoice: a cheque that bounced, a chargeback, a refund.
 *
 * **The reason is required here and optional on a void**, which is not an inconsistency. A voided
 * invoice is one nobody ever owed; a reversed payment is money that arrived and left again, and the
 * reason is the only thing that says whether somebody should be chasing the customer, the bank, or
 * nobody at all. It is stored and shown on the row afterwards.
 *
 * **The write-off is a separate question from the amount**, because no figure can answer it. A
 * chargeback a college will make good is an open bill again; the same chargeback from a program
 * that has shut down is money gone. The admin says which; the arithmetic cannot.
 */
export const ReverseInvoicePaymentModal = ({
  invoice,
  saving,
  error,
  onConfirm,
  onClose,
}: ReverseInvoicePaymentModalProps) => {
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [uncollectible, setUncollectible] = useState(false);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [amountError, setAmountError] = useState<string | null>(null);

  const received = invoice?.paidAmountCents ?? 0;

  useEffect(() => {
    if (!invoice) return;
    setReason('');
    // Everything that was received, which is the ordinary case: a cheque bounces whole.
    setAmount(centsToDollarsInput(invoice.paidAmountCents ?? 0));
    setUncollectible(false);
    setReasonError(null);
    setAmountError(null);
  }, [invoice]);

  const handleConfirm = () => {
    const trimmedReason = reason.trim();
    const amountCents = dollarsToCents(amount);

    /*
      Both checks run before either returns, so an admin who has got two things wrong is told about
      both at once rather than fixing one and being shown the next.
    */
    const nextReasonError = trimmedReason
      ? null
      : 'Say why the money came back. It is the only record of it.';

    const nextAmountError =
      amountCents === null || amountCents <= 0
        ? 'Enter the amount that came back, in dollars.'
        : amountCents > received
          ? `That is more than the ${formatMoney(received, invoice?.currency)} recorded against this invoice.`
          : null;

    setReasonError(nextReasonError);
    setAmountError(nextAmountError);
    if (nextReasonError || nextAmountError || amountCents === null) return;

    onConfirm({ reason: trimmedReason, amountCents, uncollectible });
  };

  return (
    <ConfirmModal
      isOpen={invoice !== null}
      title="Reverse a payment"
      message={
        invoice
          ? `Take back money recorded against the ${formatMoney(
              invoice.amountCents,
              invoice.currency
            )} invoice. The payment stays on the record as reversed rather than being erased.`
          : ''
      }
      confirmText={saving ? 'Saving...' : 'Reverse payment'}
      busy={saving}
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <div className="billing-form">
        <TextInput
          label="Reason"
          placeholder="Cheque returned unpaid"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={reasonError ?? undefined}
          disabled={saving}
          data-testid="billing-reverse-reason"
        />
        <TextInput
          label="Amount coming back (dollars)"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={amountError ?? undefined}
          disabled={saving}
          data-testid="billing-reverse-amount"
        />
        <Checkbox
          label="The money is not coming. Write the balance off."
          checked={uncollectible}
          onChange={(e) => setUncollectible(e.target.checked)}
          disabled={saving}
          data-testid="billing-reverse-uncollectible"
        />
        <p className="billing-panel__hint">
          {uncollectible
            ? 'The invoice is marked uncollectible. It stops counting towards what this organization owes and stops being chased, and it stays on the record.'
            : 'The invoice goes back to open and is chased again.'}
        </p>
        {error && (
          <p className="billing-form__error" role="alert" data-testid="billing-reverse-error">
            {error}
          </p>
        )}
      </div>
    </ConfirmModal>
  );
};
